import { ActionIcon, Box, Button, Group, Modal, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconPencil, IconTrash } from '@tabler/icons-react';
import { DataTable, DataTableColumn } from 'mantine-datatable';
import React, { CSSProperties, useState } from 'react';
import { textOrNotRecorded } from '../notRecorded';

export type CashflowColumn<T> = {
  key: keyof T;
  header: string;
  align?: 'left' | 'right';
  renderValue?: (value: T[keyof T], item: T) => React.ReactNode;
};

type CashflowTableProps<T> = {
  items: T[];
  columns: CashflowColumn<T>[];
  onEdit?: (item: T) => void;
  onDelete?: (item: T) => void;
};

export default function CashflowTable<T extends Record<string, unknown>>({
  items,
  columns,
  onEdit,
  onDelete,
}: CashflowTableProps<T>) {
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] =
    useDisclosure(false);
  const [itemToDelete, setItemToDelete] = useState<T | null>(null);

  const handleDeleteClick = (item: T) => {
    setItemToDelete(item);
    openDeleteModal();
  };

  const handleDeleteConfirm = () => {
    if (itemToDelete && onDelete) {
      onDelete(itemToDelete);
    }
    closeDeleteModal();
    setItemToDelete(null);
  };

  const dataTableColumns: DataTableColumn<T>[] = [
    ...columns.map(col => ({
      accessor: String(col.key),
      title: col.header,
      textAlign: (col.align || 'left') as 'left' | 'right',
      cellsStyle: () => CELL_STYLE,
      render: (record: T) => renderCell(col, record),
    })),
    ...(onEdit || onDelete
      ? [
          {
            accessor: 'actions',
            title: '',
            textAlign: 'right' as const,
            width: 100,
            cellsStyle: () => CELL_STYLE,
            render: (record: T) => (
              <Group gap="xs" justify="flex-end">
                {onEdit && (
                  <ActionIcon
                    variant="subtle"
                    color="gray.8"
                    onClick={() => onEdit(record)}
                    aria-label="Edit"
                  >
                    <IconPencil size={16} />
                  </ActionIcon>
                )}
                {onDelete && (
                  <ActionIcon
                    variant="subtle"
                    color="gray.8"
                    onClick={() => handleDeleteClick(record)}
                    aria-label="Delete"
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                )}
              </Group>
            ),
          },
        ]
      : []),
  ];

  return (
    <Box>
      <DataTable
        columns={dataTableColumns}
        records={items}
        variant="cashflows-table"
        withTableBorder={false}
        minHeight={150}
        style={
          { '--mantine-datatable-row-border-color': 'var(--mantine-color-gray-1)' } as CSSProperties
        }
        rowStyle={() => ({ height: 56 })}
      />

      <Modal opened={deleteModalOpened} onClose={closeDeleteModal} title="Are you sure?" centered>
        <Text c="gray.10">Are you sure you want to remove this item?</Text>
        <Group justify="flex-end" mt="xl">
          <Button variant="pill" onClick={closeDeleteModal}>
            Cancel
          </Button>
          <Button variant="pill" color="red" onClick={handleDeleteConfirm}>
            Remove
          </Button>
        </Group>
      </Modal>
    </Box>
  );
}

const CELL_STYLE = { paddingTop: 16, paddingBottom: 8 } as const;

function renderCell<T extends Record<string, unknown>>(col: CashflowColumn<T>, record: T) {
  if (col.renderValue) return col.renderValue(record[col.key], record);
  return textOrNotRecorded(record[col.key]);
}
